/**
 * ContactSellerModal
 * Multi-step modal for contacting a marketplace seller:
 *   1. If signed in → jump straight to review/adjust
 *   2. If guest → enter email → verify with 6-digit code → review/adjust
 * The compose step shows qty/price adjustment per item and auto-generates the message.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import {
  sendVerificationCode,
  confirmVerificationCode,
  startConversation,
  isSignedIn,
  getStoredGuestMsgToken,
  getStoredGuestMsgEmail,
  clearGuestMsgToken,
} from '../services/messagingService';

export interface ReviewItem {
  id: number;
  name: string;
  qty: number;
  maxQty: number;
  price: number;
  originalPrice: number;
  /** When true, price cannot be changed (Buy Now items) */
  priceLocked?: boolean;
}

interface ContactSellerModalProps {
  open: boolean;
  onClose: () => void;
  sellerId: number;
  sellerName: string;
  collectionId: number;
  /** Pre-selected items with qty/price to adjust */
  reviewItems?: ReviewItem[];
  /** Single item context when clicking from an item card */
  itemId?: number;
  itemName?: string;
}

type Step = 'email' | 'code' | 'compose' | 'sent';

const ContactSellerModal: React.FC<ContactSellerModalProps> = ({
  open,
  onClose,
  sellerId,
  sellerName,
  collectionId,
  reviewItems,
  itemId,
  itemName,
}) => {
  const [step, setStep] = useState<Step>(() => {
    if (isSignedIn() || getStoredGuestMsgToken()) return 'compose';
    return 'email';
  });
  const [email, setEmail] = useState(() => getStoredGuestMsgEmail() || '');
  const [code, setCode] = useState('');
  const [guestName, setGuestName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  // Editable items for the review/adjust form
  const [items, setItems] = useState<ReviewItem[]>(() => reviewItems ? [...reviewItems] : []);

  const codeInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Steps with input fields sit just above the keyboard; 'sent' is a normal bottom-sheet
  const hasInputs = step !== 'sent';
  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  // Start with an estimated keyboard height so the panel is positioned correctly on first frame
  const [kbHeight, setKbHeight] = useState(() => hasInputs ? 300 : 0);
  const [headerBottom, setHeaderBottom] = useState(80); // fallback

  useEffect(() => {
    if (!open || !hasInputs) { setKbHeight(0); return; }
    // Set initial estimate immediately
    setKbHeight(300);
    // Measure the unified header bottom edge
    const hdr = document.querySelector('.unified-header');
    if (hdr) setHeaderBottom(hdr.getBoundingClientRect().bottom);
    if (isIOSNative) {
      const { Keyboard } = require('@capacitor/keyboard');
      const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
        setKbHeight(info.keyboardHeight || 300);
      });
      const hideListener = Keyboard.addListener('keyboardWillHide', () => {
        setKbHeight(300); // keep the gap even when keyboard hides briefly
      });
      return () => {
        showListener.then((l: any) => l.remove());
        hideListener.then((l: any) => l.remove());
      };
    } else {
      const vv = window.visualViewport;
      if (!vv) return;
      const onResize = () => {
        const offset = window.innerHeight - vv.height;
        setKbHeight(offset > 50 ? offset : 300);
      };
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      return () => {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      };
    }
  }, [open, hasInputs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus code input when step changes
  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setError('');
      setCode('');
      setLoading(false);
      setCodeSent(false);
      setItems(reviewItems ? [...reviewItems] : []);
      if (isSignedIn() || getStoredGuestMsgToken()) {
        setStep('compose');
      } else {
        setStep('email');
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildMessageFromItems(): string {
    if (items.length > 0) {
      const lines = items.map(
        (i) => `  #${i.id} — ${i.name} × ${i.qty} — $${i.price.toFixed(2)}${i.price !== i.originalPrice ? ` (offer — listed at $${i.originalPrice.toFixed(2)})` : ''}`,
      );
      const total = items.reduce((s, i) => s + i.price * i.qty, 0);
      return [
        `Hi ${sellerName},`,
        '',
        `I'd like to purchase the following items:`,
        '',
        ...lines,
        '',
        `Total: $${total.toFixed(2)}`,
        '',
      ].join('\n');
    }
    if (itemName) {
      return `Hi ${sellerName},\n\nI'm interested in "${itemName}". Is it still available?\n`;
    }
    return `Hi ${sellerName},\n\n`;
  }

  // ---- Step handlers ----

  async function handleSendCode() {
    setError('');
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    const res = await sendVerificationCode(email.trim().toLowerCase());
    setLoading(false);
    if (!res.success) {
      setError(res.error || 'Failed to send code.');
      return;
    }
    setCodeSent(true);
    setStep('code');
  }

  async function handleVerifyCode() {
    setError('');
    if (code.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setLoading(true);
    const res = await confirmVerificationCode(email.trim().toLowerCase(), code);
    setLoading(false);
    if (!res.success) {
      setError(res.error || 'Invalid code.');
      return;
    }
    setStep('compose');
  }

  async function handleSend() {
    setError('');
    const body = buildMessageFromItems().trim();
    if (!body) {
      setError('No items to send.');
      return;
    }
    setLoading(true);
    const res = await startConversation({
      recipient_id: sellerId,
      collection_id: collectionId,
      item_id: itemId,
      message: body,
      guest_name: guestName.trim() || undefined,
    });
    setLoading(false);
    if (!res.success) {
      setError(res.error || 'Failed to send message.');
      return;
    }
    setStep('sent');
  }

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-primary)',
          borderRadius: hasInputs ? '16px' : '16px 16px 0 0',
          width: hasInputs ? 'calc(100% - 16px)' : '100%',
          maxHeight: hasInputs
            ? `${window.innerHeight - kbHeight - 4 - headerBottom - 8}px`
            : '85vh',
          marginBottom: hasInputs ? `${kbHeight + 4}px` : 0,
          overflow: 'auto',
          padding: '20px 16px',
          paddingBottom: hasInputs ? '20px' : `calc(20px + env(safe-area-inset-bottom, 0px))`,
          boxShadow: '0 4px 30px rgba(0,0,0,0.15)',
          transition: 'margin-bottom 0.2s ease-out, max-height 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
            {step === 'sent' ? 'Message Sent' : `Contact ${sellerName}`}
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '24px', color: 'var(--color-text-muted)', cursor: 'pointer', padding: '4px' }}
          >×</button>
        </div>

        {/* ---- STEP: EMAIL ---- */}
        {step === 'email' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              Enter your email to get started. We'll send you a verification code.
            </p>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
              autoFocus
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
                fontSize: '15px',
                outline: 'none',
              }}
            />
            {error && <p style={{ margin: 0, fontSize: '13px', color: '#ef4444' }}>{error}</p>}
            <button
              onClick={handleSendCode}
              disabled={loading}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--color-accent)',
                color: '#fff',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Sending…' : 'Send Verification Code'}
            </button>
          </div>
        )}

        {/* ---- STEP: CODE ---- */}
        {step === 'code' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              We sent a 6-digit code to <strong>{email}</strong>.
            </p>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
                fontSize: '22px',
                fontWeight: '600',
                letterSpacing: '8px',
                textAlign: 'center',
                outline: 'none',
              }}
            />
            {error && <p style={{ margin: 0, fontSize: '13px', color: '#ef4444' }}>{error}</p>}
            <button
              onClick={handleVerifyCode}
              disabled={loading || code.length !== 6}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--color-accent)',
                color: '#fff',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || code.length !== 6 ? 0.7 : 1,
              }}
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              onClick={() => { setStep('email'); setCode(''); setError(''); }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Use a different email
            </button>
          </div>
        )}

        {/* ---- STEP: COMPOSE (review/adjust items) ---- */}
        {step === 'compose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Guest identity indicator with 'Not you?' option */}
            {!isSignedIn() && getStoredGuestMsgEmail() && (
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span>Messaging as <strong style={{ color: 'var(--color-text-secondary)' }}>{getStoredGuestMsgEmail()}</strong></span>
                <button
                  onClick={() => { clearGuestMsgToken(); setEmail(''); setStep('email'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                >Not you?</button>
              </div>
            )}
            {/* Guest name (optional, only for guests) */}
            {!isSignedIn() && (
              <div>
                <label style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '4px', display: 'block' }}>Your name (optional)</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {/* Items list with qty/price adjustment */}
            {items.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {items.map((item, idx) => (
                  <div key={item.id} style={{
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border)',
                    background: item.price !== item.originalPrice ? 'rgba(249, 115, 22, 0.08)' : 'var(--color-bg-secondary)',
                  }}>
                    <div style={{ fontWeight: '500', fontSize: '15px', color: 'var(--color-text-primary)', marginBottom: '8px' }}>
                      #{item.id} — {item.name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                      {/* Qty stepper */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <label style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginRight: '2px' }}>Qty</label>
                        <button
                          onClick={() => setItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.max(1, r.qty - 1) } : r))}
                          disabled={item.qty <= 1}
                          style={{
                            width: '28px', height: '28px', borderRadius: '6px',
                            border: '1px solid var(--color-border)',
                            background: item.qty <= 1 ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)',
                            color: item.qty <= 1 ? 'var(--color-text-faint)' : 'var(--color-text-primary)',
                            fontSize: '16px', fontWeight: '600',
                            cursor: item.qty <= 1 ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                          }}
                        >−</button>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={item.qty}
                          key={`qty-${item.id}-${item.qty}`}
                          onBlur={(e) => {
                            const val = Math.min(item.maxQty, Math.max(1, parseInt(e.target.value) || 1));
                            e.target.value = String(val);
                            setItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: val } : r));
                          }}
                          style={{
                            width: '36px', padding: '4px 2px', borderRadius: '6px',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)',
                            fontSize: '14px', textAlign: 'center',
                          }}
                        />
                        <button
                          onClick={() => setItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.min(r.maxQty, r.qty + 1) } : r))}
                          disabled={item.qty >= item.maxQty}
                          style={{
                            width: '28px', height: '28px', borderRadius: '6px',
                            border: '1px solid var(--color-border)',
                            background: item.qty >= item.maxQty ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)',
                            color: item.qty >= item.maxQty ? 'var(--color-text-faint)' : 'var(--color-text-primary)',
                            fontSize: '16px', fontWeight: '600',
                            cursor: item.qty >= item.maxQty ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                          }}
                        >+</button>
                        {item.maxQty > 1 && (
                          <span style={{ fontSize: '11px', color: 'var(--color-text-faint)', marginLeft: '2px' }}>
                            /{item.maxQty}
                          </span>
                        )}
                      </div>
                      {/* Price */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <label style={{ fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>$</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          defaultValue={item.price.toFixed(2)}
                          key={`price-${item.id}`}
                          disabled={!!item.priceLocked}
                          onBlur={(e) => {
                            if (item.priceLocked) return;
                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                            e.target.value = val.toFixed(2);
                            setItems(prev => prev.map((r, i) => i === idx ? { ...r, price: val } : r));
                          }}
                          style={{
                            width: '64px', padding: '6px 6px', borderRadius: '6px',
                            border: `1px solid ${item.priceLocked ? 'var(--color-success)' : item.price !== item.originalPrice ? 'var(--color-warning)' : 'var(--color-border)'}`,
                            background: item.priceLocked ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)',
                            color: item.priceLocked ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                            fontSize: '14px', textAlign: 'center',
                          }}
                        />
                      </div>
                      {/* Subtotal */}
                      <span style={{ marginLeft: 'auto', fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        ${(item.qty * item.price).toFixed(2)}
                      </span>
                    </div>
                    {/* Price indicator */}
                    {item.priceLocked ? (
                      <div style={{ fontSize: '12px', color: 'var(--color-success)', marginTop: '6px' }}>
                        Buy Now price
                      </div>
                    ) : item.price !== item.originalPrice ? (
                      <div style={{ fontSize: '12px', color: 'var(--color-warning)', marginTop: '6px' }}>
                        Offer — listed at ${item.originalPrice.toFixed(2)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {/* Single item (no review items) — just show item name */}
            {items.length === 0 && itemName && (
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                Interested in <strong>{itemName}</strong>
              </p>
            )}

            {/* Total + Send */}
            <div style={{ borderTop: items.length > 0 ? '1px solid var(--color-border)' : 'none', paddingTop: items.length > 0 ? '12px' : 0 }}>
              {items.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-text-primary)' }}>Total</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                    ${items.reduce((sum, i) => sum + i.price * i.qty, 0).toFixed(2)}
                  </span>
                </div>
              )}
              {error && <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#ef4444' }}>{error}</p>}
              <button
                onClick={handleSend}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? 'Sending…' : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ---- STEP: SENT ---- */}
        {step === 'sent' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
            <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', margin: '0 0 8px 0' }}>
              Your message has been sent to <strong>{sellerName}</strong>.
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: '0 0 20px 0' }}>
              {isSignedIn()
                ? "You'll see their reply in your inbox."
                : "They'll reply to your email."}
            </p>
            <button
              onClick={onClose}
              style={{
                padding: '12px 32px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};


export default ContactSellerModal;
